import "../../styles/global.css";
import DownloadableTable from "./DataTable";
import { fetchQuestionAverageByStudent, fetchDemoQuestionAverageByStudent } from "../../api/individualApi";
import { fetchAllSubmissionDates, fetchAllDemoSubmissionDates, fetchCourseName, fetchCourseSections } from "../../api/overviewApi";
import React, { useState, useEffect, useMemo } from "react";
import { Typography, Select, MenuItem, Button, Chip, Tooltip,
         Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { fetchAlerts, postAlertOverride, ALERT_CATEGORIES, triggerChiron, fetchChironRun,
    fetchStudentNotes, postStudentNote } from "../../api/alertsApi";
import { studentAliasMap } from "../Constants.js";

const CATEGORY_GROUP = {
    never_attended:      'negative',
    missing_assignments: 'negative',
    low_engagement:      'negative',
    exam_performance:    'negative',
    exceptional:         'positive',
    satisfactory:        'positive',
};

const DEMO_COURSE_ID = '93298';

const CATEGORY_LABEL = {
    never_attended:      'Neg: Never Attended / No WebCampus Activity',
    missing_assignments: 'Neg: Missing Assignments',
    low_engagement:      'Neg: Lack of Engagement or Infrequent Attendance',
    exam_performance:    'Neg: Exam/Quiz Performance',
    satisfactory:        'Positive: Satisfactory Course Performance',
    exceptional:         'Positive: Exceptional Course Performance',
};

const CHIRON_UNLOCK_DATE = new Date('2026-09-08T00:00:00'); // set your target day
const isBeforeUnlock = new Date() < CHIRON_UNLOCK_DATE;

const toRangeFromDates = (dates) => {
  const ts = (Array.isArray(dates) ? dates : [])
    .map((d) => Date.parse(d))
    .filter((n) => Number.isFinite(n));
  if (ts.length === 0) return [null, null];
  return [Math.min(...ts), Math.max(...ts)];
};

const Data = () => {

    const [allDates, setAllDates] = useState([]);
    const [studentQuestionAverage, setStudentQuestionAverage] = useState([]);
    const [error, setError] = useState(null);
    const [sessionInfo, setSessionInfo] = useState(null);
    const [courseName, setCourseName] = useState("Demo Course");
    const [selectedSection, setSelectedSection] = useState("all");  
    const [sectionNumbers, setSectionNumbers] = useState([]);
    const [chironLoading, setChironLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [alertsError, setAlertsError] = useState(null);
    const [noAlertsYet, setNoAlertsYet] = useState(false);

    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    const [chironRun, setChironRun] = useState(null); 
    const [chironRunning, setChironRunning] = useState(false);

    const [openNotesFor, setOpenNotesFor] = useState(null); // canvas_user_id or null
    const [notesByStudent, setNotesByStudent] = useState({}); // { [canvas_user_id]: [{id, note, created_at}, ...] }
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNoteText, setNewNoteText] = useState('');
    const [notesError, setNotesError] = useState(null);

    useEffect(() => {
        const cached = localStorage.getItem('iTOOLS_sessionInfo');
        if (cached) {
            console.log("Using cached session info");
            setSessionInfo(JSON.parse(cached));
        } else {
            console.log("No cached session info found");
            setSessionInfo({ name: "Demo Course", courseId: '93298' }); // Fallback to demo
        }
    }, []);

    useEffect(() => {
        if (!sessionInfo) return;

        console.log("Fetching course name...");
        fetchCourseName(sessionInfo.courseId)
            .then((name) => {
                console.log("? Course name:", name);
                setCourseName(name);
            })
            .catch((err) => {
                console.error("Error fetching course name:", err);
            });
    }, [sessionInfo]);

    useEffect(() => {
        if (!sessionInfo) return;

        (async () => {
            try {
            const data = await fetchAllSubmissionDates(sessionInfo.courseId);
            const [minDate, maxDate] = toRangeFromDates(data?.dates);
            setAllDates([minDate, maxDate]);

            // Cold start: no submissions, don't call downstream APIs
            if (!Number.isFinite(minDate) || !Number.isFinite(maxDate)) {
                setStudentQuestionAverage([]);  // clear UI state
                return;
            }

            // const startISO = new Date(minDate).toISOString();
            // const endISO   = new Date(maxDate).toISOString();
            const startISO = new Date(Date.parse("2026-01-20T00:00:00-07:00")).toISOString();
            const endISO = new Date().toISOString();

            const resp = await fetchQuestionAverageByStudent(startISO, endISO, sessionInfo.courseId);
            console.log("Data tab - fetchQuestionAverageByStudent response:", resp);
            setStudentQuestionAverage(resp?.student_question_stats || []);
            } catch (error) {
            console.error("Error fetching all submission dates:", error);
            setAllDates([null, null]);
            setStudentQuestionAverage([]);
            }
        })();
    }, [sessionInfo]);

    useEffect(() => {
        if (studentQuestionAverage.length > 0) {
            console.log("Fetched studentQuestionAverage:", studentQuestionAverage);
        }
    }, [studentQuestionAverage]);

    useEffect(() => {
        if (!sessionInfo?.courseId) return;
        fetchCourseSections(sessionInfo.courseId)
            .then((data) => setSectionNumbers(data.sections ?? []))
            .catch((err) => console.error("Error fetching course sections:", err));
    }, [sessionInfo]);

    useEffect(() => {
        if (!sessionInfo?.courseId) return;
        setAlertsLoading(true);
        setAlertsError(null);
        fetchAlerts(sessionInfo.courseId)
            .then((data) => {
                setAlerts(data.alerts ?? []);
                setNoAlertsYet(!!data.noAlertsYet);
            })
            .catch(err => setAlertsError(err.message || "Failed to fetch alerts"))
            .finally(() => setAlertsLoading(false));
    }, [sessionInfo]);

    useEffect(() => {
        if (!sessionInfo?.courseId) return;
        const load = () => {
            fetchChironRun(sessionInfo.courseId)
                .then(setChironRun)
                .catch(err => console.error("Error fetching Chiron run status:", err));
        };
        load();
    }, [sessionInfo]);

    useEffect(() => {
        if (chironRun?.status !== "pending") return;

        const interval = setInterval(() => {
            fetchChironRun(sessionInfo.courseId).then(run => {
                setChironRun(run);
                if (run?.status !== "pending") {
                    setChironRunning(false);
                    // re-fetch alerts after Chiron run completes
                    fetchAlerts(sessionInfo.courseId)
                        .then((data) => setAlerts(data.alerts ?? []));
                }
            });
        }, 10000); // Poll every 10 seconds
        return () => clearInterval(interval);
    }, [chironRun, sessionInfo]);

    useEffect(() => {
        if (!sessionInfo?.courseId) return;
        fetchChironRun(sessionInfo.courseId)
            .then(run => {
                console.log("chironRun:", run);
                setChironRun(run);
            })
            .catch(err => console.error("Error fetching chiron run:", err));
    }, [sessionInfo]);

    const DATE_FIELDS = new Set(['last_active']);
    const NUMERIC_FIELDS = new Set([
        'current_grade', 'quiz_score', 'missing_assignments', 'total_clicks',
        'click_coverage_z', 'click_intensity_z', 'click_coherence_z'
    ])

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleToggleNotes = async (canvasUserId) => {
        if (openNotesFor === canvasUserId) {
            setOpenNotesFor(null);
            return;
        }
        setOpenNotesFor(canvasUserId);
        setNewNoteText('');
        setNotesError(null);

        if (!notesByStudent[canvasUserId]) {
            setNotesLoading(true);
            try {
                const data = await fetchStudentNotes(sessionInfo.courseId, canvasUserId);
                setNotesByStudent(prev => ({ ...prev, [canvasUserId]: data.notes ?? [] }));
            } catch (error) {
                setNotesError(error.message || "Failed to load notes");
            } finally {
                setNotesLoading(false);
            }
        }
    };

    const handleAddNote = async (canvasUserId) => {
        if (!newNoteText.trim()) return;
        try {
            const saved = await postStudentNote(sessionInfo.courseId, canvasUserId, newNoteText.trim());
            setNotesByStudent(prev => ({
                ...prev,
                [canvasUserId]: [{ id: saved.id, note: saved.note, created_at: saved.created_at }, ...(prev[canvasUserId] ?? [])],
            }));
            setNewNoteText('');
        } catch (error) {
            setNotesError(error.message || "Failed to save note");
        }
    };



    const reshapedData = useMemo(() => {
        const studentMap = {};
        for (const row of studentQuestionAverage) {
            if (selectedSection !== "all" && row.section_number !== selectedSection) continue;
            const name = row.student_name || "Unknown";
            const code = row.group_code || "Unknown";
            const score = parseFloat(row.avg_score) || 0;

            if (!studentMap[name]) {
                studentMap[name] = { 
                    USER_NAME: name,
                    COURSE_DESCRIPTION: courseName || "Demo Course",
                };
            }

            studentMap[name][code] = score;
        }
        return Object.values(studentMap);
    }, [studentQuestionAverage, selectedSection, courseName]);

    const filteredAlerts = useMemo(() => {
        const bySection = selectedSection === "all"
            ? alerts
            : alerts.filter((row) => String(row.section_number) === String(selectedSection));

        const isDemo = String(sessionInfo?.courseId) === DEMO_COURSE_ID;

        if (!isDemo) {
            return bySection; // real courses: no alias filtering/remapping
        }

        return bySection
            .filter((row) => studentAliasMap[row.student_name] != null)
            .map((row) => ({
                ...row,
                student_name: studentAliasMap[row.student_name],
            }));
    }, [alerts, selectedSection, sessionInfo]);
    
    const sortedAlerts = useMemo(() => {
        if (!sortField) return filteredAlerts;

        const sorted = [...filteredAlerts].sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (DATE_FIELDS.has(sortField)) {
                valA = valA ? Date.parse(valA) : 0;
                valB = valB ? Date.parse(valB) : 0;
            }

            // null/undefined always sort to the end, regardless of sort direction
            if (valA == null && valB == null) return 0;
            if (valA == null) return 1;
            if (valB == null) return -1;

            let cmp;
            if (NUMERIC_FIELDS.has(sortField) || DATE_FIELDS.has(sortField)) {
                cmp = valA - valB;
            } else {
                cmp = String(valA).localeCompare(String(valB));
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });
        return sorted;
    }, [filteredAlerts, sortField, sortDirection]);

    const SortableHeader = ({ field, children }) => (
        <TableSortLabel
            active={sortField === field}
            direction={sortField === field ? sortDirection : 'asc'}
            onClick={() => handleSort(field)}
            sx={{
                '& .MuiTableSortLabel-icon': {
                    opacity: sortField === field ? 1 : 0.4,
                },
            }}
        >
            {children}
        </TableSortLabel>
    );

    const handleChironDownload = async () => {
        if (!sessionInfo?.courseId) {
            alert("Course information not available yet.");
            return;
        }
        setChironLoading(true);
        try {
            const res = await fetch(`https://api.olaitools.com/api/chiron-alerts/${sessionInfo.courseId}/latest`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                alert(err.error || "No Chiron alerts available yet for this course.");
                return;
            }
            const { url } = await res.json();
            window.open(url, "_blank");
        } catch (err) {
            console.error("Error fetching Chiron alerts file:", err);
            alert("Failed to fetch Chiron alerts file.");
        } finally {
            setChironLoading(false);
        }
    };

    const handleGenerateChiron = async () => {
        if (!sessionInfo?.courseId) {
            alert("Course information not available yet.");
            return;
        }
        setChironRunning(true);
        try {
            const run = await triggerChiron(sessionInfo.courseId);
            setChironRun(run);
        } catch (error) {
            console.error("Error triggering Chiron:", error);
            setChironRunning(false);
        }
    };

    const handleCSVDownload = () => {
        const headers = ['Student Name', 'NSHE ID', 'Category', 'Source', 'Notes', 'Quiz Score', 'Missing', 'Clicks', 'Coverage Z', 'Intensity Z', 'Coherence Z', 'Last Active'];
        const rows = alerts.map(r => [
            r.student_name,
            r.nshe_id ?? '',
            r.category,
            r.entry_type,
            r.notes ?? '',
            r.quiz_score ?? '',
            r.missing_assignments,
            r.total_clicks,
            r.click_coverage_z ?? '',
            r.click_intensity_z ?? '',
            r.click_coherence_z ?? '',
            r.last_active ?? '',
        ]);
        const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chiron_alerts_${sessionInfo.courseId}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleCategoryChange = async (canvasUserId, newCategory) => {
        const previous = alerts;
        setAlerts(prev => prev.map(row =>
            row.canvas_user_id === canvasUserId
                ? { ...row, category: newCategory, entry_type: 'instructor' }
                : row
        ));
        try {
            await postAlertOverride(sessionInfo.courseId, canvasUserId, newCategory);
        } catch (error) {
            console.error("Error posting alert override:", error);
            setAlerts(previous); // roll back
            setAlertsError(`Override failed: ${error.message}`);
        }
    };

    console.log("Data - reshapedData:", reshapedData);
    return (
        <div className="wrapper">
            <div style={{ marginTop: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
                    <h2 className="title" style={{ margin: 0 }}>Early Alerts</h2>
                    <Typography variant="body1">
                        These alerts are generated by Chiron, an automated early-alert system powered by agentic AI. Chiron reviews each student's grades, missing assignments, and Canvas activity, and sorts students into categories below based on rules the UNLV Academic Advising team has set. Click "Run Chiron" to generate the latest alerts, and you can review or override any category at any time.
                    </Typography>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", marginTop: '1rem', marginBottom: '1.5rem' }}>
                    {sectionNumbers.length > 1 && (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                            <Typography variant="subtitle1"><strong>Filter by:</strong></Typography>
                            <Select
                                value={selectedSection}
                                onChange={(e) => setSelectedSection(e.target.value)}
                                size="small"
                                style={{ minWidth: 160 }}
                            >
                                <MenuItem value="all">All Sections</MenuItem>
                                {sectionNumbers.map((sec) => (
                                    <MenuItem key={sec} value={sec}>
                                        Section {sec}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    )}
                    {/* --- alerts section --- */}
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleGenerateChiron}
                        disabled={isBeforeUnlock || chironRunning || chironRun?.status === "pending"}
                        sx={{
                            backgroundColor: '#f59e0b',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#d97706' },
                            '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                        }}
                    >
                        {chironRun?.status === "pending" ? "Chiron Running..." : "Run Chiron"}
                    </Button>

                    {chironRun && chironRun.status !== "pending" && (
                        <Typography variant="body2" style={{ color: '#000' }}>
                            Last run: {new Date(chironRun.run_at).toLocaleString()}
                            {chironRun.status === 'failed' && ' — failed'}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleCSVDownload}
                        disabled={!alerts || alerts.length === 0}
                        sx={{
                            backgroundColor: '#f59e0b',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#d97706' },
                            '&.Mui-disabled': { backgroundColor: '#e5e7eb', color: '#9ca3af' },
                        }}
                    >
                        Download CSV
                    </Button>
                </div>
            

                {alertsLoading && <p>Loading alerts...</p>}
                {noAlertsYet && !alertsLoading && (
                    <Typography variant="body2" style={{ color: '#555', fontStyle: 'italic', marginTop: '0.5rem' }}>
                        Chiron needs about a week of Canvas activity to generate its first alerts for this course.
                        Once students have enough grade and engagement data on record, "Run Chiron" will activate
                        and alerts will start showing up here.
                    </Typography>
                )}
                {alertsError   && <p style={{ color: "red" }}>Error: {alertsError}</p>}

                {!alertsLoading && alerts.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><SortableHeader field="student_name">Student</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="current_grade">Grade</SortableHeader></TableCell>
                                <TableCell>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                        <SortableHeader field="category">Category</SortableHeader>
                                        <Tooltip
                                            title="Some students are also flagged by a separate predictive model using factors outside Canvas (e.g. self-reported outside commitments, proxy GPA). For these students, Chiron is more likely to place them into a negative-alert category."
                                            placement="bottom"
                                            arrow
                                            componentsProps={{ tooltip: { sx: { maxWidth: 320, fontSize: '0.85rem', lineHeight: 1.4, padding: '0.75rem 1rem' } } }}
                                        >
                                            <InfoOutlinedIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                                        </Tooltip>
                                    </div>
                                </TableCell>
                                <TableCell><SortableHeader field="entry_type">Source</SortableHeader></TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell><SortableHeader field="quiz_score">Exam/Quiz Average</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="missing_assignments">Missing Assignments</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="total_clicks">#Clicks</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="click_coverage_z">Coverage Z</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="click_intensity_z">Intensity Z</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="click_coherence_z">Coherence Z</SortableHeader></TableCell>
                                <TableCell><SortableHeader field="last_active">Last Active</SortableHeader></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                return sortedAlerts.flatMap(row => {
                                    const rows = [];

                                    rows.push(
                                        <TableRow key={row.canvas_user_id}>
                                            <TableCell>{row.student_name || "Unknown"}</TableCell>
                                            <TableCell>{row.current_grade ?? '—'}</TableCell>
                                            <TableCell>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <Select
                                                        value={row.category}
                                                        onChange={(e) => handleCategoryChange(row.canvas_user_id, e.target.value)}
                                                        size="small"
                                                        sx={{
                                                            width: 260,
                                                            '& .MuiSelect-select': {
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            },
                                                            ...(row.at_risk_predicted && {
                                                                backgroundColor: 'rgba(245,158,11,0.08)',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#f59e0b',
                                                                },
                                                            }),
                                                        }}
                                                    >
                                                        {ALERT_CATEGORIES.map((cat) => (
                                                            <MenuItem key={cat} value={cat}>
                                                                {CATEGORY_LABEL[cat] ?? cat}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {row.at_risk_predicted && (
                                                        // <Tooltip title={`Model flag: (${Math.round(row.at_risk_probability * 100)}%)`}>
                                                        <Tooltip title={`Flagged by predictive model`}>
                                                            <InfoOutlinedIcon
                                                                fontSize="small"
                                                                sx={{ color: '#f59e0b' }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.entry_type === 'instructor' ? 'Instructor' : 'Chiron'}
                                                    color={row.entry_type === 'instructor' ? 'primary' : 'default'}
                                                    size="small"
                                                    variant={row.entry_type === 'instructor' ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Notes">
                                                    <EditOutlinedIcon
                                                        fontSize="small"
                                                        onClick={() => handleToggleNotes(row.canvas_user_id)}
                                                        sx={{ cursor: 'pointer', color: openNotesFor === row.canvas_user_id ? '#f59e0b' : '#9ca3af' }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{row.quiz_score ?? '—'}</TableCell>
                                            <TableCell>{row.missing_assignments}</TableCell>
                                            <TableCell>{row.total_clicks}</TableCell>
                                            <TableCell>{row.click_coverage_z  ?? '—'}</TableCell>
                                            <TableCell>{row.click_intensity_z ?? '—'}</TableCell>
                                            <TableCell>{row.click_coherence_z ?? '—'}</TableCell>
                                            <TableCell>{row.last_active ?? '—'}</TableCell>
                                        </TableRow>
                                    );

                                    if (openNotesFor === row.canvas_user_id) {
                                        rows.push(
                                            <TableRow key={`${row.canvas_user_id}-notes`}>
                                                <TableCell colSpan={12} style={{ backgroundColor: '#fafafa' }}>
                                                    <div style={{ width: '100%', boxSizing: 'border-box' }}>
                                                        {notesLoading && <Typography variant="body2">Loading notes...</Typography>}
                                                        {notesError && <Typography variant="body2" style={{ color: 'red' }}>{notesError}</Typography>}
                                                        <div style={{ marginBottom: '0.75rem' }}>
                                                            <input
                                                                type="text"
                                                                value={newNoteText}
                                                                onChange={(e) => setNewNoteText(e.target.value)}
                                                                placeholder="Add a note..."
                                                                style={{ width: '100%', padding: '0.4rem 0.6rem', boxSizing: 'border-box', marginBottom: '0.5rem' }}
                                                            />
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => handleAddNote(row.canvas_user_id)}
                                                            >
                                                                Add
                                                            </Button>
                                                        </div>
                                                        {(notesByStudent[row.canvas_user_id] ?? []).map(n => (
                                                            <div key={n.id} style={{ marginBottom: '0.5rem' }}>
                                                                <Typography variant="caption" style={{ color: '#888' }}>
                                                                    {new Date(n.created_at).toLocaleString()}
                                                                </Typography>
                                                                <Typography variant="body2">{n.note}</Typography>
                                                            </div>
                                                        ))}
                                                        {!notesLoading && (notesByStudent[row.canvas_user_id] ?? []).length === 0 && (
                                                            <Typography variant="body2" style={{ color: '#888', fontStyle: 'italic' }}>
                                                                No notes yet.
                                                            </Typography>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    }

                                    return rows;
                                });
                            })()}
                        </TableBody>
                            
                    </Table>
                 )}
            </div>
        </div>
    );
};

export default Data;